// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { Dealer_Viewable_Driver_Info, Moves } from '@gql/base-types';
import { useGetMoveDetailsDriverLazyQuery } from '../queries/getMoveDetailsDriver.query.generated';
import { useSubscribeToMoveDetailsSubscription } from '../queries/subscribeToMoveDetails.subscription.generated';

import { css } from '@emotion/css';
import { Theme, useTheme, LinearProgress, Container } from '@mui/material';

import MoveDetailsContent from '@features/moves/components/MoveDetailsContent';
import MoveFloatingChat from '@features/moves/components/MoveFloatingChat';
import { useChatModal } from '@components/Chat/ChatModalManager';
import { isMoveLite } from '@utils/tiers';

// COMPONENT ---------------------------------------------------------------- //

const MoveDetailsPage = () => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const { id: paramMoveId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { openChat } = useChatModal();

  const [getDriver] = useGetMoveDetailsDriverLazyQuery();

  const [driver, setDriver] = React.useState<Dealer_Viewable_Driver_Info>({});

  const { error, loading, data } = useSubscribeToMoveDetailsSubscription({
    variables: { moveId: parseInt(paramMoveId || '0') },
  });

  // const { subscribeToMore, error, loading, data } = useGetMoveDetailsQuery({
  //   variables: { moveId: parseInt(paramMoveId || '0') },
  // });

  // // Subscribe to move details
  // React.useEffect(() => {
  //   const unsubscribe = subscribeToMore({
  //     document: SubscribeToMoveDetailsDocument,
  //     variables: { moveId: parseInt(paramMoveId || '0') },
  //     updateQuery: (prev, { subscriptionData }) => {
  //       if (!subscriptionData.data) return prev;
  //       return {
  //         ...prev,
  //         moves_by_pk: subscriptionData.data.moves_by_pk,
  //       };
  //     },
  //   });
  //   return unsubscribe;
  // }, [subscribeToMore, paramMoveId]);

  // Set the move as a local constant
  const move: Moves = data?.moves_by_pk;

  // Fetch the dealer viewable driver info on initial load
  React.useEffect(() => {
    const fetchDriver = async () => {
      if (!paramMoveId) return;

      const driverRes = await getDriver({ variables: { moveId: parseInt(paramMoveId) } });
      if (driverRes?.data?.dealer_viewable_driver_info?.length) {
        setDriver(driverRes?.data?.dealer_viewable_driver_info?.[0]);
      }
    };
    fetchDriver();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramMoveId]);

  // Refetch driver info when move.driver_id changes (e.g., after assignment/unassignment)
  React.useEffect(() => {
    const refetchDriver = async () => {
      if (!paramMoveId || !move?.driver_id) {
        // Clear driver if no driver is assigned
        setDriver({});
        return;
      }

      const driverRes = await getDriver({ variables: { moveId: parseInt(paramMoveId) } });
      if (driverRes?.data?.dealer_viewable_driver_info?.length) {
        setDriver(driverRes?.data?.dealer_viewable_driver_info?.[0]);
      } else {
        // Clear driver if no driver info found
        setDriver({});
      }
    };

    // Only refetch if we have a move loaded
    if (move?.id) {
      refetchDriver();
    }
  }, [move?.driver_id, move?.id, paramMoveId, getDriver]);

  // Handle auto-opening chat from URL parameter (only for a self-managed (lite) move)
  React.useEffect(() => {
    const openChatParam = searchParams.get('openChat');
    if (openChatParam === 'true' && move?.id && move?.consumer_pickup && isMoveLite(move)) {
      // Open chat modal
      openChat(move.id, move.consumer_pickup);
      // Remove the parameter from URL to keep it clean
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('openChat');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [move?.id, move?.consumer_pickup, move?.lite, searchParams, setSearchParams, openChat]);

  // Render the loading component
  if (error || loading) return <LinearProgress />;

  // Render the move details page
  return (
      <div className={cls.pageContainer}>
        <Container maxWidth='lg'>
          <MoveDetailsContent move={move} driver={driver} />
        </Container>
        
        {/* Floating Chat Widget — only for a self-managed (lite) move */}
        {isMoveLite(move) && (
          <MoveFloatingChat 
            moveId={move?.id} 
            consumerPickup={move?.consumer_pickup || false} 
          />
        )}
      </div>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme?: Theme) => ({
  pageContainer: css`
  padding: ${theme?.spacing(3)} 0;
  min-height: 100vh;
`,
});

// EXPORT ---------------------------------------------------------------- //

export default MoveDetailsPage;
